import React, { useState, useEffect, FC } from 'react'
import { Form, Input, Button, Select, Spin } from 'antd'
import { FormComponentProps } from 'antd/lib/form'
import { EndCustomer, EndCustomerFormValues, Reseller } from '../../types'
import styles from './index.module.less'
import { resellerService } from '../../services'
import RequestError from '../../services/RequestError'
import handleError from '../handleError'

interface EndCustomerAccountFormProps extends FormComponentProps {
	reseller?: Reseller
	endCustomer?: EndCustomer
	submit(values: EndCustomerFormValues): Promise<void>
	cancel?(): void
}

const EndCustomerAccountForm: FC<EndCustomerAccountFormProps> = props => {
	const [resellerLoading, setResellerLoading] = useState<boolean>(false)
	const [isSaving, setIsSaving] = useState<boolean>(false)
	const [resellers, setResellers] = useState<Reseller[]>([])

	const hasEndCustomer = typeof props.endCustomer !== 'undefined'

	const onSubmit = (): void => {
		setIsSaving(true)
		props.form.validateFields(
			async (errors, values): Promise<void> => {
				if (errors) {
					setIsSaving(false)
					return
				}

				try {
					await props.submit(values)
				} catch (e) {
					type Fields = Record<string, { errors: Error[]; value: string }>
					const error = e as RequestError
					const { body, status } = error.response

					switch (status) {
						case 422: {
							const entries = Object.entries(body)
							const newFields = entries.reduce<Fields>((fields, [key, errors]) => {
								fields[key] = {
									value: props.form.getFieldValue(key),
									errors: errors.map((error: string) => new Error(error))
								}
								return fields
							}, {})
							props.form.setFields(newFields)
							break
						}
						default: {
							handleError(error, () => ({
								status,
								source: 'Create End Customer',
								description: `There was an unexpected error when attempting to create a new End Customer`
							}))
							break
						}
					}
				} finally {
					setIsSaving(false)
				}
			}
		)
	}

	useEffect(() => {
		if (props.reseller) {
			return
		}
		const init = async (): Promise<void> => {
			setResellerLoading(true)
			const { data } = await resellerService.getAll()
			setResellers(data)
			setResellerLoading(false)
		}
		init()
	}, [props.reseller])

	const resellerOptions = resellers.map(reseller => (
		<Select.Option value={reseller.id} key={reseller.id}>
			{reseller.name}
		</Select.Option>
	))

	return (
		<Form className={styles.form} hideRequiredMark layout='horizontal' labelCol={{ span: 6 }} wrapperCol={{ span: 18 }} labelAlign='left'>
			{!hasEndCustomer ? (
				<Form.Item className={styles.formItem} label='Reseller'>
					{props.form.getFieldDecorator('reseller_id', {
						initialValue: props.reseller?.id || undefined,
						rules: [
							{
								required: true,
								message: 'Reseller is required'
							}
						]
					})(
						<Select
							notFoundContent={resellerLoading ? <Spin size='small' /> : 'No matches found.'}
							placeholder='Select a reseller'
							showSearch
							filterOption={(input, option) => {
								const optionName = option.props.children as string
								return optionName.toLowerCase().includes(input.toLowerCase())
							}}
							disabled={typeof props.reseller !== 'undefined'}
						>
							{props.reseller ? (
								<Select.Option value={props.reseller.id} key={props.reseller.id}>
									{props.reseller.name}
								</Select.Option>
							) : (
								resellerOptions
							)}
						</Select>
					)}
				</Form.Item>
			) : (
				props.form.getFieldDecorator('reseller_id', { initialValue: props.endCustomer.reseller_id })(<Input hidden />)
			)}
			<Form.Item className={styles.formItem} label='Customer'>
				{props.form.getFieldDecorator('name', {
					rules: [
						{
							whitespace: true,
							required: true,
							message: 'Customer is required.'
						}
					],
					initialValue: hasEndCustomer ? props.endCustomer.name : ''
				})(<Input placeholder='Enter Customer Name' autoComplete='off' type='text' name='Customer' />)}
			</Form.Item>
			<Form.Item className={styles.formItem} label='Address 1'>
				{props.form.getFieldDecorator('address1', {
					rules: [
						{
							whitespace: true,
							required: false
						}
					],
					initialValue: hasEndCustomer ? props.endCustomer.address1 : ''
				})(<Input placeholder='Enter Address 1' type='text' />)}
			</Form.Item>
			<Form.Item className={styles.formItem} label='Address 2'>
				{props.form.getFieldDecorator('address2', {
					rules: [
						{
							whitespace: true,
							required: false
						}
					],
					initialValue: hasEndCustomer ? props.endCustomer.address2 : ''
				})(<Input placeholder='Enter Address 2' type='text' />)}
			</Form.Item>
			<Form.Item className={styles.formItem} label='Postcode'>
				{props.form.getFieldDecorator('postcode', {
					rules: [
						{
							whitespace: true,
							required: false
						}
					],
					initialValue: hasEndCustomer ? props.endCustomer.postcode : ''
				})(
					<Input
						spellCheck={false}
						onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
							e.target.value = e.target.value.toUpperCase()
						}}
						placeholder='Enter Postcode'
						type='text'
					/>
				)}
			</Form.Item>
			<Form.Item className={styles.formItem} label='Description'>
				{props.form.getFieldDecorator('description', {
					initialValue: hasEndCustomer ? props.endCustomer.description : ''
				})(<Input.TextArea spellCheck={true} placeholder='Description' style={{ minHeight: '100px' }} />)}
			</Form.Item>
			<div className={styles.buttons}>
				{props.cancel && (
					<Button className={styles.cancelButton} onClick={props.cancel}>
						Cancel
					</Button>
				)}
				<Button loading={isSaving} type='primary' onClick={onSubmit}>
					{isSaving ? 'Saving' : 'Save'}
				</Button>
			</div>
		</Form>
	)
}

export default Form.create<EndCustomerAccountFormProps>()(EndCustomerAccountForm)
